/**
 * Re-pricing a OneAmerica Asset Care quote that has a 3-year AOB, from the quote itself.
 *
 * The 2-year quotes are re-priced from tables measured per person. That does not carry over:
 * every feature of the 3-year curve sits at a fixed DEATH BENEFIT, not a fixed premium, so one
 * person's curve puts those features at the wrong premium for everyone else. Measured on the
 * carrier's engine on 2026-10-13 (packages/scraping/tools/assetCarePremiumLadder.js): 368 full
 * curves and 400 randomly drawn quotes, single and joint, Single Pay and 10-Pay.
 *
 *   - The engine takes no premium whose death benefit is under $50,000.
 *   - Benefits scale exactly with the premium, with one exception: a 10-Pay quote pays a $100
 *     annual fee while its death benefit is under $100,000.
 *   - With inflation protection the engine caps the monthly benefit: $17,000 at 2%, $14,000 at
 *     3%, $10,000 at 5%. Without it, the death benefit is capped at $1,000,000.
 *   - From a $900,000 death benefit up, the death benefit grows faster than the premium and the
 *     total LTC benefit slower, by amounts that differ for every quote (0.2% to 3.2% up, 3% to
 *     6% down at $1,000,000). Nothing here predicts that, so a quote is not re-priced past
 *     $900,000. Only quotes without inflation can reach it.
 *
 * Inside the range this gives, the rule reproduced the engine on every one of 3,492 measured
 * points to within 0.008% ($11 of death benefit, $37 of total LTC benefit). The one exception is
 * the dollar on either side of the $100,000 step: the stored death benefit is a whole number,
 * which leaves the step's exact premium uncertain by about a dollar.
 *
 * The range is set a few dollars inside the engine's own limits. On the 400 drawn quotes the
 * computed minimum was $0.30 to $2.50 below the engine's, and the computed maximum within $8 of
 * it either way; in 5 cases the engine also refused the very maximum it had just stated.
 *
 * SOUTH DAKOTA, VERMONT AND WISCONSIN
 *   These three price exactly as the other states do, but sell nothing under a monthly benefit
 *   of their own: $3,000, $2,250 and $1,800, against the $1,389 a $50,000 death benefit comes
 *   to. Measured on 2026-10-13 (packages/scraping/tools/assetCareStateMinimums.js): at its own
 *   minimum premium and at the scrape's premium, each state returned Alaska's quote to the
 *   dollar. So each has a copy of the Alaska quotes under a category of its own, and the
 *   category carries the minimum. A copy whose scraped premium bought less than the state's
 *   minimum is stored at a higher premium the state does take, with the benefits this rule
 *   gives there (packages/database/scripts/load-asset-care-3-year-aob-sd-vt-wi.sql).
 */
import { FundingPolicyType } from '../appModel';
import { getQuotePayYears } from '../guidedPlanningPreferences';

/** The 39 states that take Alaska's quotes as they are. */
export const OA_THREE_YEAR_AOB_CATEGORY = 4;
/** One category per state that sets a minimum of its own. */
export const OA_THREE_YEAR_AOB_STATE_CATEGORIES = { SD: 5, VT: 6, WI: 7 } as const;
/** The smallest monthly benefit each of those states sells. */
export const OA_THREE_YEAR_AOB_STATE_MINIMUM_MONTHLY_BENEFIT = { SD: 3_000, VT: 2_250, WI: 1_800 } as const;

const AOB_MONTHS = 36;
const MINIMUM_DEATH_BENEFIT = 50_000;
const MINIMUM_DEATH_BENEFIT_BY_CATEGORY: Record<number, number> = {
  [OA_THREE_YEAR_AOB_CATEGORY]: MINIMUM_DEATH_BENEFIT,
  [OA_THREE_YEAR_AOB_STATE_CATEGORIES.SD]: OA_THREE_YEAR_AOB_STATE_MINIMUM_MONTHLY_BENEFIT.SD * AOB_MONTHS,
  [OA_THREE_YEAR_AOB_STATE_CATEGORIES.VT]: OA_THREE_YEAR_AOB_STATE_MINIMUM_MONTHLY_BENEFIT.VT * AOB_MONTHS,
  [OA_THREE_YEAR_AOB_STATE_CATEGORIES.WI]: OA_THREE_YEAR_AOB_STATE_MINIMUM_MONTHLY_BENEFIT.WI * AOB_MONTHS,
};
const FEE_WAIVED_AT_DEATH_BENEFIT = 100_000;
const TEN_PAY_ANNUAL_FEE = 100;
const STRAIGHT_LINE_ENDS_AT_DEATH_BENEFIT = 900_000;
const MONTHLY_BENEFIT_CAP_BY_INFLATION: Record<string, number> = {
  '2%': 17_000,
  '3%': 14_000,
  '5%': 10_000,
};
const MINIMUM_PREMIUM_MARGIN = 3;
const MAXIMUM_PREMIUM_MARGIN = 25;

/**
 * The rule's numbers, as the SQL loaders that store each quote's premium range repeat them
 * (packages/database/scripts/load-asset-care-3-year-aob*.sql). oneAmericaThreeYearAobLoaders.test.ts
 * holds the loaders to these.
 */
export const OA_THREE_YEAR_AOB_RULE_NUMBERS = {
  aobMonths: AOB_MONTHS,
  minimumDeathBenefit: MINIMUM_DEATH_BENEFIT,
  tenPayAnnualFee: TEN_PAY_ANNUAL_FEE,
  feeWaivedAtDeathBenefit: FEE_WAIVED_AT_DEATH_BENEFIT,
  straightLineEndsAtDeathBenefit: STRAIGHT_LINE_ENDS_AT_DEATH_BENEFIT,
  monthlyBenefitCapByInflation: MONTHLY_BENEFIT_CAP_BY_INFLATION,
  minimumPremiumMargin: MINIMUM_PREMIUM_MARGIN,
  maximumPremiumMargin: MAXIMUM_PREMIUM_MARGIN,
} as const;

type ThreeYearAobQuote = Pick<
  PolicyQuote,
  | 'fundingPolicyType'
  | 'usaStateCategory'
  | 'aobDuration'
  | 'aobInflation'
  | 'cobPaymentOption'
  | 'productFundingOption'
  | 'premiumAmount'
  | 'deathBenefitInitial'
>;

export function isThreeYearAobAssetCareQuote(quote: ThreeYearAobQuote): boolean {
  const isAssetCare =
    quote.fundingPolicyType === FundingPolicyType.assetCare ||
    quote.fundingPolicyType === FundingPolicyType.jointAssetCare;
  return (
    isAssetCare &&
    minimumDeathBenefitOf(quote) !== undefined &&
    quote.aobDuration?.trim() === '3 Years'
  );
}

function minimumDeathBenefitOf(quote: ThreeYearAobQuote): number | undefined {
  return quote.usaStateCategory ? MINIMUM_DEATH_BENEFIT_BY_CATEGORY[quote.usaStateCategory] : undefined;
}

interface Curve {
  /** Death benefit per premium dollar once any fee is out of the way. */
  rate: number;
  hasFee: boolean;
  /** The premium that buys benefits: the premium itself, less the fee where one applies. */
  payingPremium: (premium: number) => number;
}

function curveOf(quote: ThreeYearAobQuote): Curve | null {
  const premium = Number(quote.premiumAmount);
  const deathBenefit = Number(quote.deathBenefitInitial);
  if (!(premium > 0) || !(deathBenefit > 0)) return null;

  const hasFee = getQuotePayYears(quote) === 10;
  const feeAtOwnPremium = hasFee && deathBenefit < FEE_WAIVED_AT_DEATH_BENEFIT ? TEN_PAY_ANNUAL_FEE : 0;
  if (premium <= feeAtOwnPremium) return null;
  const rate = deathBenefit / (premium - feeAtOwnPremium);

  return {
    rate,
    hasFee,
    // The engine rounds the death benefit to the dollar before testing it against $100,000.
    payingPremium: target =>
      hasFee && rate * target < FEE_WAIVED_AT_DEATH_BENEFIT - 0.5 ? target - TEN_PAY_ANNUAL_FEE : target,
  };
}

function highestDeathBenefit(aobInflation: string | null | undefined): number | null {
  const inflation = aobInflation?.trim();
  if (!inflation || /^(none|0%?)$/i.test(inflation)) return STRAIGHT_LINE_ENDS_AT_DEATH_BENEFIT;
  const monthlyCap = MONTHLY_BENEFIT_CAP_BY_INFLATION[inflation];
  return monthlyCap ? monthlyCap * AOB_MONTHS : null;
}

/**
 * The premiums this quote can be re-priced to, in whole dollars, or null when the quote does not
 * carry what the rule needs (including an inflation option that was never measured).
 */
export function getThreeYearAobPremiumRange(
  quote: ThreeYearAobQuote,
): { min: number; max: number } | null {
  const curve = curveOf(quote);
  const cap = highestDeathBenefit(quote.aobInflation);
  const floor = minimumDeathBenefitOf(quote);
  if (!curve || cap === null || floor === undefined) return null;

  // A state minimum of $100,000 or more is past the point where the 10-Pay fee stops.
  const feeAtFloor = curve.hasFee && floor < FEE_WAIVED_AT_DEATH_BENEFIT ? TEN_PAY_ANNUAL_FEE : 0;
  const lowest = floor / curve.rate + feeAtFloor;
  // The quote's own premium is one the engine did accept. Where it sits inside a margin, as
  // on the few quotes scraped right at a $50,000 death benefit, the range starts or ends there.
  const ownPremium = Number(quote.premiumAmount);
  const range = {
    min: Math.min(Math.ceil(lowest) + MINIMUM_PREMIUM_MARGIN, ownPremium),
    max: Math.max(Math.floor(cap / curve.rate) - MAXIMUM_PREMIUM_MARGIN, ownPremium),
  };
  return range.min <= range.max ? range : null;
}

/**
 * How the quote's benefits change when its premium becomes `newPremium`: one factor for the
 * death benefit, the monthly benefit and the total LTC benefit alike. Null outside the range.
 */
export function getThreeYearAobScalingFactor(
  quote: ThreeYearAobQuote,
  newPremium: number,
): number | null {
  const curve = curveOf(quote);
  const range = getThreeYearAobPremiumRange(quote);
  if (!curve || !range || newPremium < range.min || newPremium > range.max) return null;

  return curve.payingPremium(newPremium) / curve.payingPremium(Number(quote.premiumAmount));
}
